import React, { SyntheticEvent } from "react"
import ReactTable, {
  SortingRule,
  Column,
  RowInfo,
  ComponentPropsGetterR,
  ComponentPropsGetter0,
  ComponentPropsGetterRC,
} from "react-table"
import {
  IRawSupplierReference,
  ISupplierReference,
  completeRawDeclined,
} from "../../models"
import { addClass, Class } from "../add-class"
import { normalizeString } from "../string"

export const defaultFilterMethod = (filter: any, row: any, column: any) => {
  // react-table also applies filters whose value is empty (they are restored from localStorage at every display):
  // an empty filter must keep every row. Otherwise the "Ref. Bruneau" filter hid every product that has no
  // Bruneau reference yet, i.e. every newly created product.
  if (filter.value === undefined || filter.value === null || `${filter.value}`.trim() === "") {
    return true
  }
  let match = false
  const orig = row._original
  const id = filter.pivotId || filter.id
  match =
    orig[id] !== undefined
      ? normalizeString(String(orig[id])).includes(
          normalizeString(filter.value)
        )
      : false

  if (orig && orig.isTopRef && orig.declined) {
    orig.declined.forEach((p: ISupplierReference) => {
      match =
        match ||
        (p[id] !== undefined
          ? normalizeString(String(p[id])).includes(
              normalizeString(filter.value)
            )
          : false)
    })
  }

  return match
}

export const onSortedChange =
  (setSorted: any) =>
  (newSorted: SortingRule[], column: any, shiftKey: boolean) => {
    setSorted([
      {
        id: "rank",
        desc: true,
      },
      ...newSorted,
    ])
  }

interface ITableTranslations {
  previousText?: string
  nextText?: string
  loadingText?: string
  noDataText?: string
  pageText?: string
  ofText?: string
  rowsText?: string
}

export const getTranslations = (translations: any) => ({
  previousText: translations.tablePreviousText,
  nextText: translations.tableNextText,
  loadingText: translations.tableLoadingText,
  noDataText: translations.tableNoDataText,
  pageText: translations.tablePageText,
  ofText: translations.tableOfText,
  rowsText: translations.tableRowsText,
})

export const topRefTableUtils = {
  columnsToJsx: (
    columns: Array<Column<IRawSupplierReference>>,
    topRef: IRawSupplierReference,
    goToProduct: (droopyId: string, declined?: string) => void,
    showDeletedRefs: boolean
  ) => {
    return topRef.declined
      ?.filter((x) => x.deleted === showDeletedRefs)
      .map((declined, i) => {
        const ref = completeRawDeclined(topRef, declined)
        const className = Class.addClass("rt-tr hoverable", getTrClassName(ref))
        return (
          <div className={className} key={i}>
            {columns.map((col) => {
              const onClick = () =>
                onTdClick({
                  columnId: col.id || "",
                  droopyId: ref.droopyId,
                  topDroopyId: ref.topDroopyId,
                  columnIsExpander: !!col.expander,
                  goToProduct,
                })

              return (
                <div
                  key={col.id}
                  id={col.id}
                  style={{
                    width: col.width,
                    maxWidth: col.width,
                    flex: "100 0 auto",
                  }}
                  role="gridcell"
                  className={`${col.className} rt-td`}
                  onClick={onClick}
                >
                  {renderColumns(col, ref)}
                </div>
              )
            })}
          </div>
        )
      })
  },

  getTrProps:
    (): ComponentPropsGetterR | ComponentPropsGetter0 =>
    (state: any, rowInfo: RowInfo | undefined) => {
      let className
      if (rowInfo) {
        const reference = rowInfo.original as IRawSupplierReference
        className = getTrClassName(reference)
        className = addClass(className, "hoverable")
      }
      return {
        className,
        onClick: (e: SyntheticEvent, handleOriginal: () => void) => {
          if (handleOriginal) {
            handleOriginal()
          }
        },
      }
    },

  getTdProps:
    (
      goToProduct: (droopyId: string, declinedIs?: string) => void
    ): ComponentPropsGetterR | ComponentPropsGetterRC =>
    (
      state: any,
      rowInfo: RowInfo | undefined,
      { id: columnId, expander: columnIsExpander }: any,
      instance: any
    ) => {
      return {
        onClick: (e: SyntheticEvent, handleOriginal: () => void) => {
          if (rowInfo) {
            onTdClick({
              columnId,
              droopyId: rowInfo.original.droopyId,
              topDroopyId: rowInfo.original.topDroopyId,
              columnIsExpander,
              goToProduct,
            })
          }

          if (handleOriginal) {
            handleOriginal()
          }
        },
      }
    },
}

const getTrClassName = (reference: IRawSupplierReference) => {
  const className = []

  //   if (reference.isIgnored) {
  //     className.push("ignored")
  //   } else if (filteredReferences.find((r) => r.refData === reference.refData)) {
  //     className.push("disabled")
  //   }

  if (reference.isTopRef) {
    className.push("top-ref")
  }
  if (reference.topRef !== undefined) {
    className.push("variant")
  }
  return className.join(" ")
}

const renderColumns = (
  col: Column<IRawSupplierReference>,
  ref: IRawSupplierReference
) => {
  if (typeof col?.accessor === "function") {
    return col.accessor(ref)
  }
  if (typeof col?.accessor === "string") {
    return ref[col.accessor]
  }
  if (col?.id) {
    return ref[col.id]
  }
  return null
}

interface IOnTdClick {
  droopyId: string
  columnId: string
  topDroopyId?: string
  columnIsExpander: boolean
  goToProduct: (droopyId: string, declined?: string) => void
}

const onTdClick = ({
  droopyId,
  columnId,
  topDroopyId,
  columnIsExpander,
  goToProduct,
}: IOnTdClick) => {
  if (columnId !== "selectButton" && !columnIsExpander) {
    if (topDroopyId) {
      goToProduct(topDroopyId, droopyId)
    } else {
      goToProduct(droopyId)
    }
  }
}
